import { z } from 'zod';
/**
 * The download centre — `/exports`. The ONE place a "complete" export is made.
 *
 * WHAT A COMPLETE EXPORT IS. Every report screen has its own Unduh Excel /
 * Unduh PDF buttons, and those stay where they are: a flat table of the rows on
 * screen, produced inside the request. A COMPLETE export is a different object —
 * the rows AND everything behind each one (a workbook per row, the photographs
 * beside it), packed as a ZIP. It takes as long as fetching every photograph
 * takes, so it is not produced inside a request: it is a JOB, queued, worked one
 * at a time, and downloaded when it is ready.
 *
 * WHY ONE MENU AND NOT A BUTTON PER SCREEN. A button that takes two minutes and
 * disables itself is a screen that looks hung; five of them is five screens that
 * look hung. The queue gives the wait a place to be looked at, and one place
 * means one set of statuses, one retention rule and one page to explain.
 */
/**
 * Which report a job packs. One member per screen that offers a complete
 * export; a member exists here BEFORE its exporter does, so the menu can list
 * every report and grey out the ones not yet built rather than growing an
 * option at a time.
 */
export const ExportJobKind = z.enum([
    /** Laporan → Kunjungan: `Kunjungan.xlsx` + a folder per visit. */
    'VISITS_DETAIL',
    /** Indeks Harga: the paired report, a folder per pair. */
    'PRICE_PAIRS_DETAIL',
    /** Indeks Harga Detail: one row per store visit. */
    'PRICE_VISITS_DETAIL',
    /** Kepatuhan Planogram: the SKU-grain report. */
    'PLANOGRAM_DETAIL',
    /** Planogram Detail: one row per store visit. */
    'PLANOGRAM_VISITS_DETAIL',
    /** Laporan POSM: a folder per visit, the POSM photographs inside it. */
    'POSM_DETAIL',
]);
/** The menu's own words for each kind — the sidebar labels of the screens they pack. */
export const EXPORT_JOB_KIND_LABELS = Object.freeze({
    VISITS_DETAIL: 'Laporan Kunjungan',
    PRICE_PAIRS_DETAIL: 'Indeks Harga',
    PRICE_VISITS_DETAIL: 'Indeks Harga Detail',
    PLANOGRAM_DETAIL: 'Kepatuhan Planogram',
    PLANOGRAM_VISITS_DETAIL: 'Planogram Detail',
    POSM_DETAIL: 'Laporan POSM',
});
/**
 * Where a job is in its life.
 *
 *   QUEUED    → waiting its turn; can be cancelled.
 *   RUNNING   → the worker has it; cannot be cancelled.
 *   READY     → the archive is in storage; downloadable until `expiresAt`.
 *   FAILED    → the worker gave up; `failureReason` says why, in the reader's words.
 *   CANCELLED → the requester withdrew it while it was still QUEUED.
 *   EXPIRED   → READY once, but the retention sweep has since removed the archive.
 *
 * FAILED and EXPIRED are both terminal and both leave the row: a queue that
 * forgets its failures is one nobody can ask "what happened to my export".
 */
export const ExportJobStatus = z.enum(['QUEUED', 'RUNNING', 'READY', 'FAILED', 'CANCELLED', 'EXPIRED']);
export const EXPORT_JOB_STATUS_LABELS = Object.freeze({
    QUEUED: 'Menunggu',
    RUNNING: 'Diproses',
    READY: 'Siap',
    FAILED: 'Gagal',
    CANCELLED: 'Dibatalkan',
    EXPIRED: 'Kedaluwarsa',
});
/**
 * How long a finished archive stays downloadable.
 *
 * Seven days, from the moment it became READY. A complete export is large — it
 * carries every photograph in its filter — and a manager downloads it once; a
 * week covers "I asked for it Friday and opened it Monday" without the bucket
 * accumulating gigabytes nobody will fetch again. The row survives the sweep as
 * EXPIRED, so the queue still says what was made and when.
 */
export const EXPORT_JOB_RETENTION_DAYS = 7;
/**
 * `POST /exports`. The filters are the SAME shape the report's own screen sends
 * to its endpoints — the download centre validates them with the report's own
 * schema, so a filter the screen accepts, the queue accepts.
 *
 * Loosely typed here on purpose: each kind has its own filter set (the visit
 * report caps its window at 90 days and takes a salesperson; the price index
 * takes two SKUs and no cap), and the server-side validation per kind is what
 * makes the request honest. A union here would restate five schemas that live
 * beside the reports they describe.
 */
export const CreateExportJobRequest = z.object({
    kind: ExportJobKind,
    filters: z.record(z.string(), z.unknown()),
});
//# sourceMappingURL=exports.js.map